import { LinearGradient } from "expo-linear-gradient";
import React from "react";
import { View, ViewStyle, StyleProp } from "react-native";
import { initialColor, Target, mixActions } from "../store/MixReducer";
import { useAppSelector } from "../../../store";
import { Icons, Sizing, useTheme } from "../../../theme";
import { Body, Button } from "../../../components";
import {
  flexDirectionToInside,
  flexDirectionToOutside,
  TargetProps,
  useItemLayout,
} from "../screens/MixScreen";
import { styles } from "../mixStyles";
import { useDispatch } from "react-redux";

export interface FieldGeneratorProps {
  style?: StyleProp<ViewStyle>;
  pressGenerates?: boolean;
}

export const FieldGenerator = ({
  style,
  pressGenerates = true,
}: FieldGeneratorProps) => {
  const generator = useAppSelector((state) => state.mix.generator);
  const { baseOnContrast } = useTheme();
  const item = useItemLayout(2);
  const dispatch = useDispatch();

  return (
    <Button
      onPress={() => dispatch(mixActions.generate())}
      disabled={!pressGenerates}
      style={[item, styles.field, { backgroundColor: generator }, style]}
    >
      <Body
        text={"Gemeinsame Farbe"}
        color={baseOnContrast(generator)}
        style={{ textAlign: "center" }}
      />
    </Button>
  );
};

export const FieldSecret = ({
  target,
  unpickable = true,
}: TargetProps & { unpickable?: boolean }) => {
  const secret = useAppSelector((state) => state.mix[target].secret);
  const name = target === "bob" ? "Bob" : "Alice";
  const { baseOnContrast } = useTheme();
  const item = useItemLayout();
  const dispatch = useDispatch();

  return (
    <Button
      onPress={() => dispatch(mixActions.unpick(target))}
      style={[
        item,
        styles.field,
        styles.secretField,
        {
          backgroundColor: secret,
          flexDirection: flexDirectionToOutside(target),
        },
      ]}
      disabled={!unpickable}
    >
      <Body
        text={name + " Geheim"}
        color={baseOnContrast(secret)}
        style={{ flex: 0 }}
      />
      <Icons.KeyOutline
        color={baseOnContrast(secret)}
        size="huge"
        style={{ margin: Sizing.cs(10) }}
      />
    </Button>
  );
};

export const FieldMixed = ({ target }: TargetProps) => {
  const mix = useAppSelector((state) => state.mix);
  const mixed = useAppSelector((state) => state.mix[target].mixed);
  const generator = useAppSelector((state) => state.mix.generator);
  const secret = useAppSelector((state) => state.mix[target].secret);
  const name = target === "bob" ? "Bob" : "Alice";
  const { baseOnContrast } = useTheme();
  const item = useItemLayout();

  const mixIfPicked = () => {
    return mix[target].hasPicked ? mixed : initialColor;
  };

  return (
    <LinearGradient
      style={[
        item,
        styles.field,
        {
          flexDirection: flexDirectionToInside(target),
        },
      ]}
      colors={[generator, mixIfPicked(), mixIfPicked(), secret]}
      locations={[0.1, 0.2, 0.8, 0.9]}
      start={[0, 0]}
      end={[1, 0]}
    >
      <Body
        style={styles.fieldBody}
        text={name + " Mix"}
        color={baseOnContrast(mixed)}
      />
      {target === "bob" ? (
        <Icons.UploadCircle
          color={baseOnContrast(mixed)}
          size="huge"
          style={{ transform: [{ scaleX: -1 }] }}
        />
      ) : (
        <Icons.Upload color={baseOnContrast(mixed)} size="huge" />
      )}
    </LinearGradient>
  );
};

export const FieldReceived = ({ target }: TargetProps) => {
  const mix = useAppSelector((state) => state.mix);
  const received = useAppSelector((state) => state.mix[target].received);
  const name = target === "bob" ? "Alice" : "Bob";
  const other = name.toLowerCase() as Target;
  const { baseOnContrast } = useTheme();
  const item = useItemLayout();

  const receivedIfOtherPicked = () => {
    return mix[other].hasPicked ? received : initialColor;
  };

  return (
    <View
      style={[
        item,
        styles.field,
        {
          backgroundColor: receivedIfOtherPicked(),
          flexDirection: flexDirectionToInside(target),
        },
      ]}
    >
      <Body
        style={styles.fieldBody}
        text={name + " Mix"}
        color={baseOnContrast(received)}
      />
      {target === "bob" ? (
        <Icons.Download color={baseOnContrast(received)} size="huge" />
      ) : (
        <Icons.DownloadCircle color={baseOnContrast(received)} size="huge" />
      )}
    </View>
  );
};

export const FieldResult = ({ target }: TargetProps) => {
  const received = useAppSelector((state) => state.mix[target].received);
  const mix = useAppSelector((state) => state.mix);
  const other = target === "bob" ? "alice" : "bob";
  const result = useAppSelector((state) => state.mix[target].result);
  const secret = useAppSelector((state) => state.mix[target].secret);
  const { baseOnContrast } = useTheme();
  const item = useItemLayout();

  const resultIfPicked = () => {
    return mix[target].hasPicked && mix[other].hasPicked
      ? result
      : initialColor;
  };

  const receivedIfOtherPicked = () => {
    return mix[other].hasPicked ? received : initialColor;
  };

  return (
    <LinearGradient
      style={[
        item,
        styles.field,
        styles.secretField,
        {
          flexDirection: flexDirectionToInside(target), // since other target used
        },
      ]}
      colors={[
        secret,
        resultIfPicked(),
        resultIfPicked(),
        receivedIfOtherPicked(),
      ]}
      locations={[0.1, 0.2, 0.8, 0.9]}
      start={[0, 0]}
      end={[1, 0]}
    >
      <Icons.Key color={baseOnContrast(result)} size="huge" />
      <Body
        style={styles.fieldBody}
        text={"Geteilt Geheim"}
        color={baseOnContrast(result)}
      />
    </LinearGradient>
  );
};
